import {formatDate} from '@angular/common';
import {SDKConfig} from '../sdk.config';

/**
 * Date utilities
 */
export abstract class DateUtils {
    public static readonly DAYS_TWO_LETTER = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    public static readonly MONTHS_NAME_SHORT = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];

    // ===============================================================
    // ===============================================================

    /**
     * Convert a Date object to a string in iso format but with local timzone informations
     * @param date Input date
     * @returns Iso string with correct timezone
     */
    public static toLocalISOString(date: Date | null | undefined): string | null | undefined {
        if (!date) {
            return date;
        }

        function pad(n: number): string {
            return n.toString().padStart(2, '0');
        }

        // 1️⃣ Get the parts of the local date/time
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1); // 0‑based → 1‑based
        const day = pad(date.getDate());
        const hour = pad(date.getHours());
        const minute = pad(date.getMinutes());
        const sec = pad(date.getSeconds());

        // 2️⃣ Compute the offset from UTC in minutes
        //    (negative if the local zone is behind UTC)
        const offsetMinutes = date.getTimezoneOffset();
        const sign = offsetMinutes <= 0 ? '+' : '-';
        const absOffset = Math.abs(offsetMinutes);
        const offsetHours = pad(Math.floor(absOffset / 60));
        const offsetMins = pad(absOffset % 60);

        // 3️⃣ Build the ISO‑8601 string
        return `${year}-${month}-${day}T${hour}:${minute}:${sec}${sign}${offsetHours}:${offsetMins}`;
    }

    /**
     * @param value The date expression: a `Date` object,  a number
     * (milliseconds since UTC epoch), or an ISO string (https://www.w3.org/TR/NOTE-datetime).
     * @param format The date/time components to include, using predefined options or a
     * custom format string.  When not provided, the `DatePipe` looks for the value using the
     * `DATE_PIPE_DEFAULT_OPTIONS` injection token (and reads the `dateFormat` property).
     * If the token is not configured, the `mediumDate` is used as a value.
     * @param timezone A timezone offset (such as `'+0430'`). When not provided, the `DatePipe`
     * looks for the value using the `DATE_PIPE_DEFAULT_OPTIONS` injection token (and reads
     * the `timezone` property). If the token is not configured, the end-user's local system
     * timezone is used as a value.
     * @param locale A locale code for the locale format rules to use.
     * When not supplied, uses the value of `LOCALE_ID`, which is `en-US` by default.
     * See [Setting your app locale](guide/i18n/locale-id).
     *
     * @see {@link DATE_PIPE_DEFAULT_OPTIONS}
     *
     * @returns A date string in the desired format.
     */
    public static format(
        value: Date | string | number | null | undefined,
        format?: string,
        timezone?: string,
        locale?: string
    ): string | null {
        if (value == null || value === '' || value !== value) return null;

        const _format = format ?? SDKConfig.dateFormat;
        const _timezone = timezone ?? undefined;
        return formatDate(value, _format, locale || `en-US`, _timezone);
    }
}
